import { httpRequest } from "../../http";
import { MediaFile } from "@models/mediaFile.model";

interface UploadPart {
    part_number: number;
    url: string;
}

interface MultipartUpload {
    upload_id: string;
    key: string;
    chunk_size: number;
    parts: UploadPart[];
}

interface CreateMediaResponse {
    media: MediaFile;
    upload: MultipartUpload;
}

const API_URL = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/media`;

export class CommandUploadMedia {
    async run(file: File, name?: string, metadata?: Record<string, any>, onProgress?: (percentage: number) => void): Promise<MediaFile> {
        // 1. Create media + get multipart upload URLs
        const response = await httpRequest<CreateMediaResponse>(`${API_URL}/`, {
            method: "POST",
            body: {
                name: name || file.name,
                content_type: file.type,
                file_size: file.size,
                ...(metadata ? { metadata } : {}),
            },
        });

        const { upload, media } = response;

        try {
            // 2. Upload each chunk to its presigned URL
            const completedParts = await this.uploadParts(file, upload, onProgress);

            // 3. Complete the multipart upload
            await httpRequest(`${API_URL}/${media.id}/complete-upload/`, {
                method: "POST",
                body: {
                    upload_id: upload.upload_id,
                    key: upload.key,
                    parts: completedParts,
                },
            });

            // 4. Re-fetch to get final storage_uri
            const refreshed = await httpRequest<MediaFile>(
                `${API_URL}/${media.id}/`,
                { method: "GET" },
            );
            return new MediaFile(refreshed);
        } catch (error) {
            // Abort on failure so S3 cleans up incomplete parts
            await httpRequest(`${API_URL}/${media.id}/abort-upload/`, {
                method: "POST",
                body: {
                    upload_id: upload.upload_id,
                    key: upload.key,
                },
            }).catch(() => {}); // best-effort abort
            throw error;
        }
    }

    private async uploadParts(
        file: File,
        upload: MultipartUpload,
        onProgress?: (percentage: number) => void,
    ): Promise<{ part_number: number; etag: string }[]> {
        const completedParts: { part_number: number; etag: string }[] = [];
        const totalParts = upload.parts.length;

        for (const part of upload.parts) {
            const start = (part.part_number - 1) * upload.chunk_size;
            const end = Math.min(start + upload.chunk_size, file.size);
            const chunk = file.slice(start, end);

            const res = await fetch(part.url, {
                method: "PUT",
                body: chunk,
            });

            if (!res.ok) {
                throw new Error(`Failed to upload part ${part.part_number}: ${res.status}`);
            }

            const etag = res.headers.get("ETag");
            if (!etag) {
                throw new Error(`No ETag returned for part ${part.part_number}`);
            }

            completedParts.push({ part_number: part.part_number, etag });
            onProgress?.(Math.round((completedParts.length / totalParts) * 100));
        }

        return completedParts;
    }
}
